import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch } from 'react-router';
import { Navigate, Route, useLocation } from 'react-router-dom-v5-compat';

import AuthenticatedRoute from 'dashboard/components/Router/AuthenticatedRoute';
import AuthenticatedRouteWithCurrentEnvironmentType from 'dashboard/components/Router/AuthenticatedRouteWithCurrentEnvironmentType';
import Http404Redirect from 'dashboard/components/Router/Http404Redirect';
import { SentryRoute, SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import DashboardAccountActionRunsRoute from 'dashboard/routes/DashboardAccountActionRunsRoute';
import DashboardAccountActionsShowRoute from 'dashboard/routes/DashboardAccountActionsShowRoute';
import DashboardAccountsRoute from 'dashboard/routes/DashboardAccountsRoute';
import DashboardAccountsShowRoute from 'dashboard/routes/DashboardAccountsShowRoute';
import DashboardAccountTypesRoute from 'dashboard/routes/DashboardAccountTypesRoute';
import DashboardAccountTypesShowRoute from 'dashboard/routes/DashboardAccountTypesShowRoute';
import DashboardApiAnalyticsRoute from 'dashboard/routes/DashboardApiAnalyticsRoute';
import DashboardApiKeysRoute from 'dashboard/routes/DashboardApiKeysRoute';
import DashboardApiKeysShowRoute from 'dashboard/routes/DashboardApiKeysShowRoute';
import DashboardApiLogsRoute from 'dashboard/routes/DashboardApiLogsRoute';
import DashboardApiLogsShowRoute from 'dashboard/routes/DashboardApiLogsShowRoute';
import DashboardApiRateLimitsRoute from 'dashboard/routes/DashboardApiRateLimitsRoute';
import DashboardCaseActionRunsRoute from 'dashboard/routes/DashboardCaseActionRunsRoute';
import DashboardCaseActionsRoute from 'dashboard/routes/DashboardCaseActionsRoute';
import DashboardCaseAnalyticsRoute from 'dashboard/routes/DashboardCaseAnalyticsRoute';
import DashboardCaseAssignmentPoliciesRoute from 'dashboard/routes/DashboardCaseAssignmentPoliciesRoute';
import DashboardCaseAssignmentPolicyShowRoute from 'dashboard/routes/DashboardCaseAssignmentPolicyShowRoute';
import DashboardCaseQueuesRoute from 'dashboard/routes/DashboardCaseQueuesRoute';
import DashboardCaseQueuesShowRoute from 'dashboard/routes/DashboardCaseQueuesShowRoute';
import DashboardCaseShowRoute from 'dashboard/routes/DashboardCaseShowRoute';
import DashboardCasesRoute from 'dashboard/routes/DashboardCasesRoute';
import DashboardCaseTemplatesRoute from 'dashboard/routes/DashboardCaseTemplatesRoute';
import DashboardCaseTemplatesShowRoute from 'dashboard/routes/DashboardCaseTemplatesShowRoute';
import DashboardContactUsRoute from 'dashboard/routes/DashboardContactUsRoute';
import DashboardDocumentViewerRoute from 'dashboard/routes/DashboardDocumentViewerRoute';
import DashboardEditorRoute from 'dashboard/routes/DashboardEditorRoute';
import DashboardEventsRoute from 'dashboard/routes/DashboardEventsRoute';
import DashboardEventsShowRoute from 'dashboard/routes/DashboardEventsShowRoute';
import DashboardExportersRoute from 'dashboard/routes/DashboardExportersRoute';
import DashboardExternalIntegrationRoute from 'dashboard/routes/DashboardExternalIntegrationRoute/DashboardExternalIntegrationRoute';
import DashboardExternalIntegrationsRoute from 'dashboard/routes/DashboardExternalIntegrationsRoute';
import DashboardGettingStartedRoute from 'dashboard/routes/DashboardGettingStartedRoute';
import DashboardGraphConfigRoute from 'dashboard/routes/DashboardGraphConfigRoute';
import DashboardGraphExplorerRoute from 'dashboard/routes/DashboardGraphExplorerRoute';
import DashboardGraphNodeConfigRoute from 'dashboard/routes/DashboardGraphNodeConfigRoute/DashboardGraphNodeConfigRoute';
import DashboardGraphQueryTemplatesRoute from 'dashboard/routes/DashboardGraphQueryTemplatesRoute';
import DashboardHomeRoute from 'dashboard/routes/DashboardHomeRoute';
import DashboardImporterDataSourceRoute from 'dashboard/routes/DashboardImporterDataSourceRoute';
import DashboardImporterRoute from 'dashboard/routes/DashboardImporterRoute';
import DashboardImporterRunDetailsRoute from 'dashboard/routes/DashboardImporterRunDetailsRoute/DashboardImporterRunDetailsRoute';
import DashboardImporterStepperRoute from 'dashboard/routes/DashboardImporterStepperRoute/DashboardImporterStepperRoute';
import DashboardImportsRoute from 'dashboard/routes/DashboardImportsRoute';
import DashboardInquiriesDomainManagerRoute from 'dashboard/routes/DashboardInquiriesDomainManagerRoute';
import DashboardInquiriesRoute from 'dashboard/routes/DashboardInquiriesRoute';
import DashboardInquiriesShowRoute from 'dashboard/routes/DashboardInquiriesShowRoute';
import DashboardInquiryAnalyticsRoute from 'dashboard/routes/DashboardInquiryAnalyticsRoute';
import DashboardInquiryFunnelDetailedViewRoute from 'dashboard/routes/DashboardInquiryFunnelDetailedViewRoute';
import DashboardInquiryInspectShowRoute from 'dashboard/routes/DashboardInquiryInspectShowRoute';
import DashboardInquiryPreviewShowRoute from 'dashboard/routes/DashboardInquiryPreviewShowRoute';
import DashboardInquiryTemplatePreviewShowRoute from 'dashboard/routes/DashboardInquiryTemplatePreviewShowRoute';
import DashboardInquiryTemplatesRoute from 'dashboard/routes/DashboardInquiryTemplatesRoute';
import DashboardInquiryTemplatesShowRoute from 'dashboard/routes/DashboardInquiryTemplatesShowRoute';
import DashboardInsightQueriesRoute from 'dashboard/routes/DashboardInsightQueriesRoute';
import DashboardInsightQueryToolRoute from 'dashboard/routes/DashboardInsightQueryToolRoute';
import DashboardIntegrationsRoute from 'dashboard/routes/DashboardIntegrationsRoute';
import DashboardIntegrationsShowRoute from 'dashboard/routes/DashboardIntegrationsShowRoute';
import DashboardListsRoute from 'dashboard/routes/DashboardListsRoute';
import DashboardListsShowRoute from 'dashboard/routes/DashboardListsShowRoute';
import DashboardLoginMfaRoute from 'dashboard/routes/DashboardLoginMfaRoute';
import DashboardLoginRoute from 'dashboard/routes/DashboardLoginRoute';
import DashboardLoginSamlEmailRoute from 'dashboard/routes/DashboardLoginSamlEmailRoute';
import DashboardLoginSamlSlugRoute from 'dashboard/routes/DashboardLoginSamlSlugRoute';
import DashboardLoginWorkrampRoute from 'dashboard/routes/DashboardLoginWorkrampRoute';
import DashboardNoEnvironmentHomeRoute from 'dashboard/routes/DashboardNoEnvironmentHomeRoute';
import DashboardOrganizationBillingRoute from 'dashboard/routes/DashboardOrganizationBillingRoute';
import DashboardOrganizationEmailAddressesRoute from 'dashboard/routes/DashboardOrganizationEmailAddressesRoute';
import DashboardOrganizationInformationRoute from 'dashboard/routes/DashboardOrganizationInformationRoute';
import DashboardOrganizationSecurityRoute from 'dashboard/routes/DashboardOrganizationSecurityRoute';
import DashboardPasswordResetConfirmRoute from 'dashboard/routes/DashboardPasswordResetConfirmRoute';
import DashboardPasswordResetRoute from 'dashboard/routes/DashboardPasswordResetRoute';
import DashboardPlatformConnectionsShowRoute from 'dashboard/routes/DashboardPlatformConnectionsShowRoute';
import DashboardRegisterActivateRoute from 'dashboard/routes/DashboardRegisterActivateRoute';
import DashboardRegisterConfirmRoute from 'dashboard/routes/DashboardRegisterConfirmRoute';
import DashboardRegisterRoute from 'dashboard/routes/DashboardRegisterRoute';
import DashboardReportRunsShowRoute from 'dashboard/routes/DashboardReportRunsShowRoute/DashboardReportRunsShowRoute';
import DashboardReportsAnalyticsRoute from 'dashboard/routes/DashboardReportsAnalyticsRoute';
import DashboardReportsRoute from 'dashboard/routes/DashboardReportsRoute';
import DashboardReportsShowRoute from 'dashboard/routes/DashboardReportsShowRoute';
import DashboardReportsSnapshotRoute from 'dashboard/routes/DashboardReportsSnapshotRoute';
import DashboardReportTemplatesRoute from 'dashboard/routes/DashboardReportTemplatesRoute';
import DashboardReportTemplatesShowRoute from 'dashboard/routes/DashboardReportTemplatesShowRoute';
import DashboardReportTemplateVersionsShowRoute from 'dashboard/routes/DashboardReportTemplateVersionsShowRoute';
import DashboardRoute from 'dashboard/routes/DashboardRoute';
import DashboardSandboxHomeRoute from 'dashboard/routes/DashboardSandboxHomeRoute';
import DashboardSARConfigurationShowRoute from 'dashboard/routes/DashboardSARConfigurationShowRoute';
import DashboardSARShowRoute from 'dashboard/routes/DashboardSARShowRoute';
import DashboardSARsRoute from 'dashboard/routes/DashboardSARsRoute';
import DashboardSignupActivateRoute from 'dashboard/routes/DashboardSignupActivateRoute';
import DashboardTeamShowRoute from 'dashboard/routes/DashboardTeamShowRoute';
import DashboardTeamsRoute from 'dashboard/routes/DashboardTeamsRoute';
import DashboardThemeSetsEditorRoute from 'dashboard/routes/DashboardThemeSetsEditorRoute';
import DashboardThemeSetsRoute from 'dashboard/routes/DashboardThemeSetsRoute';
import DashboardTransactionAnalyticsRoute from 'dashboard/routes/DashboardTransactionAnalyticsRoute';
import DashboardTransactionsRoute from 'dashboard/routes/DashboardTransactionsRoute';
import DashboardTransactionsShowRoute from 'dashboard/routes/DashboardTransactionsShowRoute';
import DashboardTransactionStatusesRoute from 'dashboard/routes/DashboardTransactionStatusesRoute';
import DashboardTransactionTypesRoute from 'dashboard/routes/DashboardTransactionTypesRoute';
import DashboardTransactionTypesShowRoute from 'dashboard/routes/DashboardTransactionTypesShowRoute';
import DashboardUserAcceptInviteRoute from 'dashboard/routes/DashboardUserAcceptInviteRoute';
import DashboardUserAuditLogsRoute from 'dashboard/routes/DashboardUserAuditLogsRoute';
import DashboardUserAuditLogsShowRoute from 'dashboard/routes/DashboardUserAuditLogsShowRoute';
import DashboardUserProfileRoute from 'dashboard/routes/DashboardUserProfileRoute';
import DashboardUserRolesRoute from 'dashboard/routes/DashboardUserRolesRoute';
import DashboardUserRolesShowRoute from 'dashboard/routes/DashboardUserRolesShowRoute';
import DashboardUserSessionsRoute from 'dashboard/routes/DashboardUserSessionsRoute';
import DashboardUsersRoute from 'dashboard/routes/DashboardUsersRoute';
import DashboardVerificationAnalyticsRoute from 'dashboard/routes/DashboardVerificationAnalyticsRoute';
import DashboardVerificationsRoute from 'dashboard/routes/DashboardVerificationsRoute';
import DashboardVerificationsShowRoute from 'dashboard/routes/DashboardVerificationsShowRoute';
import DashboardVerificationTemplatesRoute from 'dashboard/routes/DashboardVerificationTemplatesRoute';
import DashboardVerificationTemplatesShowRoute from 'dashboard/routes/DashboardVerificationTemplatesShowRoute';
import DashboardWebhookEventsRoute from 'dashboard/routes/DashboardWebhookEventsRoute';
import DashboardWebhookEventsShowRoute from 'dashboard/routes/DashboardWebhookEventsShowRoute';
import DashboardWebhooksRoute from 'dashboard/routes/DashboardWebhooksRoute';
import DashboardWebhooksShowRoute from 'dashboard/routes/DashboardWebhooksShowRoute';
import DashboardWorkflowModulesShowRoute from 'dashboard/routes/DashboardWorkflowModulesShowRoute';
import DashboardWorkflowRunsRoute from 'dashboard/routes/DashboardWorkflowRunsRoute';
import DashboardWorkflowRunsShowRoute from 'dashboard/routes/DashboardWorkflowRunsShowRoute';
import DashboardWorkflowRunStepsRoute from 'dashboard/routes/DashboardWorkflowRunStepsRoute';
import DashboardWorkflowsRoute from 'dashboard/routes/DashboardWorkflowsRoute';
import DashboardWorkflowsShowRoute, {
  DashboardWorkflowsShowVersionRoute,
} from 'dashboard/routes/DashboardWorkflowsShowRoute';
import DashboardIntegrationLogsShowView from 'dashboard/views/DashboardIntegrationLogsShowView/DashboardIntegrationLogsShowView';
import DashboardIntegrationLogsView from 'dashboard/views/DashboardIntegrationLogsView';

const DashboardResourcesChecksTableRoute = lazy(
  () => import('dashboard/routes/DashboardResourcesChecksTableRoute'),
);
const DashboardResourcesCoverageMapRoute = lazy(
  () => import('dashboard/routes/DashboardResourcesCoverageMapRoute'),
);

const TitledRoute = ({ title, route }: { title: string; route: React.ReactNode }) => {
  return (
    <>
      <Helmet title={title}>
        <title>{title}</title>
      </Helmet>
      {route}
    </>
  );
};

const Router = () => {
  const location = useLocation();
  // NOTE: routes that are not `exact` have nested routes (usually tabs)
  return (
    <Suspense fallback={null}>
      <SentryRoutes>
        {/* Signup */}
        <Route
          element={<TitledRoute title='Accept invite' route={<DashboardUserAcceptInviteRoute />} />}
          path='/dashboard/user/accept-invite/*'
        />
        <Route
          element={<TitledRoute title='Register' route={<DashboardRegisterRoute />} />}
          path='/dashboard/register'
        />
        <Route
          element={<TitledRoute title='Confirm' route={<DashboardRegisterConfirmRoute />} />}
          path='/dashboard/register/confirm'
        />
        <Route
          element={<TitledRoute title='Activate' route={<DashboardRegisterActivateRoute />} />}
          path='/dashboard/register/activate'
        />
        <Route
          element={
            <TitledRoute title='Register' route={<Navigate to='/dashboard/register' replace />} />
          }
          path='/dashboard/signup'
        />
        {/* These route still exists to handle expired activation links from the old flow */}
        <Route
          element={<TitledRoute title='Activate' route={<DashboardSignupActivateRoute />} />}
          path='/dashboard/signup/activate'
        />
        {/* Login */}
        <Route
          element={<TitledRoute title='Login' route={<DashboardLoginRoute />} />}
          path='/dashboard/login'
        />
        <Route
          element={<TitledRoute title='Login' route={<DashboardLoginSamlEmailRoute />} />}
          path='/dashboard/login-saml-email'
        />
        <Route
          element={<TitledRoute title='Login' route={<DashboardLoginSamlSlugRoute />} />}
          path='/dashboard/login-saml-slug'
        />
        <Route
          element={<TitledRoute title='Login' route={<DashboardLoginMfaRoute />} />}
          path='/dashboard/login-2fa'
        />
        <Route
          element={<TitledRoute title='Login' route={<DashboardLoginWorkrampRoute />} />}
          path='/dashboard/login-workramp'
        />
        {/* Password Reset */}
        <Route
          element={<TitledRoute title='Reset password' route={<DashboardPasswordResetRoute />} />}
          path='/dashboard/password-reset'
        />
        <Route
          element={
            <TitledRoute
              title='Confirm password reset'
              route={<DashboardPasswordResetConfirmRoute />}
            />
          }
          path='/dashboard/password-reset/confirm'
        />
        <Route
          element={<TitledRoute title='Contact Us' route={<DashboardContactUsRoute />} />}
          path='/dashboard/contact-us'
        />
        <Route
          path='*'
          element={
            <Switch location={location}>
              {
                // ------------------------------
                // AUTHENTICATED ROUTES
                // ------------------------------
              }
              <AuthenticatedRoute
                component={DashboardRoute}
                exact
                path='/dashboard'
                title='Dashboard'
                v6
              />
              {/* HOMEPAGE */}
              <AuthenticatedRouteWithCurrentEnvironmentType
                componentProduction={DashboardHomeRoute}
                componentSandbox={DashboardSandboxHomeRoute}
                componentNoEnvironment={DashboardNoEnvironmentHomeRoute}
                exact
                path='/dashboard/home'
                title='Home'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImporterDataSourceRoute}
                path='/dashboard/imports/data-sources/:dataSourceId'
                title='Imports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImporterStepperRoute}
                path='/dashboard/imports/importers/new'
                title='Imports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImporterStepperRoute}
                path='/dashboard/imports/importers/edit/:importerId'
                title='Imports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImporterRunDetailsRoute}
                path='/dashboard/imports/importers/:importerId/:importerRunId/run-details'
                title='Imports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImporterRoute}
                path='/dashboard/imports/importers/:importerId'
                title='Imports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImportsRoute}
                exact
                path='/dashboard/imports'
                title='Imports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardImportsRoute}
                exact
                path='/dashboard/imports/*'
                title='Imports'
                v6
              />
              {/* IDENTITY - Inquiries */}
              <AuthenticatedRoute
                component={DashboardInquiriesRoute}
                exact
                path='/dashboard/inquiries'
                title='Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiriesDomainManagerRoute}
                exact
                path='/dashboard/inquiry-manage-domain'
                title='Inquiries - Domain Manager'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryPreviewShowRoute}
                exact={false}
                path='/dashboard/inquiries/:inquiryId/preview'
                title='Inquiries - Preview'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryInspectShowRoute}
                exact={false}
                path='/dashboard/inquiry-templates/:inquiryTemplateId/:inquiryTemplateVersionId/inspect'
                title='Inquiries - Inspect'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiriesShowRoute}
                exact={false}
                path='/dashboard/inquiries/:inquiryId'
                title='Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryFunnelDetailedViewRoute}
                exact
                path='/dashboard/inquiry-analytics/:inquiryTemplateId/funnel-flow/:inquiryTemplateVersionId/:stepIds'
                title='Analytics - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryAnalyticsRoute}
                path='/dashboard/inquiry-analytics'
                title='Analytics - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardEditorRoute}
                exact={false}
                path='/dashboard/editor'
                title='Theme Editor - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardThemeSetsEditorRoute}
                exact={false}
                path='/dashboard/theme-sets/:themeSetId'
                title='Theme Set Editor'
                v6
              />
              <AuthenticatedRoute
                component={DashboardThemeSetsRoute}
                exact
                path='/dashboard/theme-sets'
                title='Theme Sets'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryTemplatesRoute}
                exact
                path='/dashboard/inquiry-templates'
                title='Templates - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryTemplatePreviewShowRoute}
                exact={false}
                path='/dashboard/inquiry-templates/:inquiryTemplateId/:inquiryTemplateVersionId/preview'
                title='Templates - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryTemplatesShowRoute}
                exact={false}
                path='/dashboard/inquiry-templates/:inquiryTemplateId/:inquiryTemplateVersionId'
                title='Templates - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInquiryTemplatesShowRoute}
                exact={false}
                path='/dashboard/inquiry-templates/:inquiryTemplateId'
                title='Templates - Inquiries'
                v6
              />
              <AuthenticatedRoute
                component={DashboardExportersRoute}
                exact
                path='/dashboard/exporters'
                title='Exporters'
                v6
              />
              {/* Data Infra - Insight Queries */}
              <AuthenticatedRoute
                component={DashboardInsightQueriesRoute}
                path='/dashboard/insight-queries'
                title='Insight Query'
                v6
              />
              <AuthenticatedRoute
                component={DashboardInsightQueryToolRoute}
                path='/dashboard/insight-query-tool'
                title='Insight Query Editor'
                v6
              />
              {/* IDENTITY - Document Viewer */}
              <AuthenticatedRoute
                component={DashboardDocumentViewerRoute}
                exact={false}
                path='/dashboard/document-viewer/:documentId'
                title='Document Viewer'
                v6
              />
              {/* IDENTITY - Verifications */}
              <AuthenticatedRoute
                component={DashboardVerificationsRoute}
                exact
                path='/dashboard/verifications'
                title='Verifications'
                v6
              />
              <AuthenticatedRoute
                component={DashboardVerificationAnalyticsRoute}
                path='/dashboard/verification-analytics'
                title='Analytics - Verifications'
                v6
              />
              <AuthenticatedRoute
                component={DashboardVerificationsShowRoute}
                path='/dashboard/verifications/:verificationId'
                title='Verifications'
                v6
              />
              <AuthenticatedRoute
                component={DashboardVerificationTemplatesRoute}
                exact
                path='/dashboard/verification-templates'
                title='Templates - Verifications'
                v6
              />
              <AuthenticatedRoute
                component={DashboardVerificationTemplatesShowRoute}
                exact={false}
                path='/dashboard/verification-templates/:verificationTemplateId/:verificationTemplateVersionId?'
                title='Templates - Verifications'
                v6
              />
              {/* IDENTITY - Accounts */}
              <AuthenticatedRoute
                component={DashboardAccountsRoute}
                exact
                path='/dashboard/accounts'
                title='Accounts'
                v6
              />
              <AuthenticatedRoute
                component={DashboardAccountsShowRoute}
                path='/dashboard/accounts/:accountOrReferenceId'
                title='Accounts'
                v6
              />
              <AuthenticatedRoute
                component={DashboardAccountTypesRoute}
                exact
                path='/dashboard/account-types'
                title='Types - Accounts'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunsShowRoute}
                exact
                path='/dashboard/account-types/:accountTypeId/actions/:accountActionId/runs/:workflowRunId'
                title='Actions - Types - Accounts'
                v6
              />
              <AuthenticatedRoute
                component={DashboardAccountActionRunsRoute}
                path='/dashboard/account-types/:accountTypeId/actions/:accountActionId/runs'
                title='Actions - Types - Accounts'
                v6
              />
              <AuthenticatedRoute
                component={DashboardAccountActionsShowRoute}
                exact
                path='/dashboard/account-types/:accountTypeId/actions/:accountActionId/:accountActionWorkflowVersionId'
                title='Actions - Types - Accounts'
                v6
              />
              <AuthenticatedRoute
                component={DashboardAccountTypesShowRoute}
                exact={false} // NOTE: this route has nested routes
                path='/dashboard/account-types/:accountTypeId'
                title='Types - Accounts'
                v6
              />
              {/* IDENTITY - Transactions */}
              <AuthenticatedRoute
                component={DashboardTransactionsRoute}
                exact
                path='/dashboard/transactions'
                title='Transactions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTransactionsShowRoute}
                path='/dashboard/transactions/:transactionId'
                title='Transactions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTransactionTypesRoute}
                exact
                path='/dashboard/transaction-types'
                title='Types - Transactions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTransactionAnalyticsRoute}
                exact
                path='/dashboard/transaction-analytics/volume'
                title='Analytics - Transactions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTransactionTypesShowRoute}
                exact={false} // NOTE: this route has nested routes
                path='/dashboard/transaction-types/:transactionTypeId'
                title='Types - Transactions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTransactionTypesShowRoute}
                exact
                path='/dashboard/transaction-types/:transactionTypeId/inactive-fields'
                title='Types - Transactions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTransactionStatusesRoute}
                exact
                path='/dashboard/transaction-types/:transactionTypeId/statuses'
                title='Statuses - Types - Transactions'
                v6
              />
              {/* IDENTITY - Graph */}
              <AuthenticatedRoute
                component={DashboardGraphConfigRoute}
                exact
                path='/dashboard/graph-configuration'
                title='Configuration - Graph'
                v6
              />
              <AuthenticatedRoute
                component={DashboardGraphNodeConfigRoute}
                exact={false} // NOTE: this route has nested routes
                path='/dashboard/graph-configuration/:graphDatumTypeId'
                title='Configuration - Graph'
                v6
              />
              <AuthenticatedRoute
                component={DashboardGraphExplorerRoute}
                exact
                path='/dashboard/explorer'
                title='Graph'
                v6
              />
              <AuthenticatedRoute
                component={DashboardGraphQueryTemplatesRoute}
                exact
                path='/dashboard/graph-query-templates'
                title='Graph'
                v6
              />
              {/* IDENTITY - Reports */}
              <AuthenticatedRoute
                component={DashboardReportsRoute}
                exact
                path='/dashboard/reports'
                title='Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportsSnapshotRoute}
                path='/dashboard/report-snapshot/:snapshotId'
                title='Snapshot - Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportRunsShowRoute}
                exact
                path='/dashboard/reports/:reportId/runs/:reportRunId'
                title='Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportsShowRoute}
                path='/dashboard/reports/:reportId'
                title='Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportTemplatesRoute}
                exact
                path='/dashboard/report-templates'
                title='Templates - Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportTemplatesShowRoute}
                exact
                path='/dashboard/report-templates/:reportTemplateId'
                title='Templates - Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportTemplateVersionsShowRoute}
                exact={false}
                path='/dashboard/report-templates/:reportTemplateId/:reportTemplateVersionId'
                title='Templates - Reports'
                v6
              />
              <AuthenticatedRoute
                component={DashboardReportsAnalyticsRoute}
                exact
                path='/dashboard/reports-analytics'
                title='Analytics - Reports'
                v6
              />
              {/* PLATFORM - Cases */}
              <AuthenticatedRoute
                component={DashboardCasesRoute}
                exact
                path='/dashboard/cases'
                title='Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseShowRoute}
                path='/dashboard/cases/:caseId'
                title='Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseAssignmentPoliciesRoute}
                exact
                path='/dashboard/case-assignment-policies'
                title='Case Assignment Policies'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseAssignmentPolicyShowRoute}
                path='/dashboard/case-assignment-policies/:caseAssignmentPolicyId'
                title='Case Assignment Policies'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseQueuesRoute}
                exact
                path='/dashboard/case-queues'
                title='Case Queues'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseQueuesShowRoute}
                path='/dashboard/case-queues/:caseQueueId'
                title='Case Assignment Policies'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseAnalyticsRoute}
                path='/dashboard/case-analytics'
                title='Analytics - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseTemplatesRoute}
                exact
                path='/dashboard/case-templates'
                title='Templates - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseTemplatesShowRoute}
                exact={false} // NOTE: this route has nested routes
                path='/dashboard/case-templates/:caseTemplateId'
                title='Templates - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseActionsRoute}
                exact
                path='/dashboard/case-actions'
                title='Actions - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunsShowRoute}
                path='/dashboard/case-actions/:workflowId/runs/:workflowRunId'
                title='Actions - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardCaseActionRunsRoute}
                path='/dashboard/case-actions/:actionWorkflowId/runs'
                title='Actions - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowsShowRoute}
                path='/dashboard/case-actions/:workflowId/:workflowVersionId'
                title='Actions - Cases'
                v6
              />
              <AuthenticatedRoute
                component={DashboardSARsRoute}
                exact
                path='/dashboard/sars'
                title='SARs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardSARConfigurationShowRoute}
                exact
                path='/dashboard/sar-configurations'
                title='Configurations - SARs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardSARShowRoute}
                exact
                path='/dashboard/sars/:sarId'
                title='SARs'
                v6
              />
              {/* PLATFORM - Workflows */}
              <AuthenticatedRoute
                component={DashboardWorkflowModulesShowRoute}
                path='/dashboard/workflows/modules/:workflowModuleId'
                title='Workflow Modules'
                v6
              />
              <AuthenticatedRoute
                component={DashboardPlatformConnectionsShowRoute}
                path='/dashboard/platform-connections/:platformNodeId'
                title='Platform Connections'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowsRoute}
                exact
                path='/dashboard/workflows'
                title='Workflows'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowsRoute}
                exact
                path='/dashboard/workflows/modules'
                title='Workflows'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunsShowRoute}
                path='/dashboard/workflows/:workflowId/runs/:workflowRunId'
                title='Runs - Workflows'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunsRoute}
                path='/dashboard/workflows/:workflowId/runs'
                title='Runs - Workflows'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowsShowVersionRoute}
                path='/dashboard/workflows/:workflowId/:workflowVersionId'
                title='Workflows'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowsShowRoute}
                path='/dashboard/workflows/:workflowId'
                title='Workflows'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunsRoute}
                exact
                path='/dashboard/workflow-runs'
                title='Workflow Runs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunsShowRoute}
                exact
                path='/dashboard/workflow-runs/:workflowRunId'
                title='Workflow Runs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWorkflowRunStepsRoute}
                exact
                path='/dashboard/workflow-steps/:workflowStepId'
                title='Workflow Steps'
                v6
              />
              {/* PLATFORM - Events */}
              <AuthenticatedRoute
                component={DashboardEventsRoute}
                exact
                path='/dashboard/events'
                title='Events'
                v6
              />
              <AuthenticatedRoute
                component={DashboardEventsShowRoute}
                exact={false}
                path='/dashboard/events/:eventId'
                title='Events'
                v6
              />
              {/* PLATFORM - Lists */}
              <AuthenticatedRoute
                component={DashboardListsRoute}
                exact
                path='/dashboard/lists'
                title='Lists'
                v6
              />
              <AuthenticatedRoute
                component={DashboardListsShowRoute}
                path='/dashboard/lists/:listId'
                title='Lists'
                v6
              />
              {/* PLATFORM - Marketplace */}
              <AuthenticatedRoute
                component={DashboardExternalIntegrationsRoute}
                exact
                path='/dashboard/marketplace'
                title='Marketplace'
                v6
              />
              <AuthenticatedRoute
                component={DashboardExternalIntegrationRoute}
                path='/dashboard/marketplace/:externalIntegration'
                title='Marketplace'
                v6
              />
              {/* PLATFORM - Integrations */}
              <AuthenticatedRoute
                component={DashboardIntegrationsRoute}
                exact
                path='/dashboard/integrations'
                title='Integrations'
                v6
              />
              <AuthenticatedRoute
                component={DashboardIntegrationsShowRoute}
                exact
                path='/dashboard/integrations/:vendorKey'
                title='Integrations'
                v6
              />
              <AuthenticatedRoute
                component={DashboardIntegrationLogsView}
                exact
                path='/dashboard/integration-logs'
                title='Integration Logs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardIntegrationLogsShowView}
                exact
                path='/dashboard/integration-logs/:integrationLogId'
                title='Integration Logs'
                v6
              />
              {/* DEVELOPERS - Integration */}
              {/* "Getting Started" used to be called "Integration" */}
              <AuthenticatedRoute
                component={() => <Navigate to='/dashboard/getting-started/' replace />}
                path='/dashboard/integration'
                redirectOnMissingCurrentEnvironment={false}
                title='Getting Started'
                v6
              />
              <AuthenticatedRoute
                component={DashboardGettingStartedRoute}
                path='/dashboard/getting-started'
                redirectOnMissingCurrentEnvironment={false}
                title='Getting Started'
                v6
              />
              {/* DEVELOPERS - API */}
              <AuthenticatedRoute
                component={DashboardApiAnalyticsRoute}
                exact={true}
                path='/dashboard/api-analytics'
                title='API analytics'
                v6
              />
              <AuthenticatedRoute
                component={DashboardApiKeysRoute}
                exact={true}
                path='/dashboard/api-keys'
                title='API keys'
                v6
              />
              <AuthenticatedRoute
                component={DashboardApiKeysShowRoute}
                exact={false}
                path='/dashboard/api-keys/:apiKeyId'
                title='API keys'
                v6
              />
              <AuthenticatedRoute
                component={DashboardApiLogsRoute}
                exact={true}
                path='/dashboard/api-logs'
                title='API logs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardApiLogsShowRoute}
                exact={false}
                path='/dashboard/api-logs/:apiLogId'
                title='API logs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardApiRateLimitsRoute}
                exact={true}
                path='/dashboard/api-rate-limits'
                title='API rate limits'
                v6
              />
              {/* DEVELOPERS - Webhooks */}
              <AuthenticatedRoute
                component={DashboardWebhooksRoute}
                exact={true}
                path='/dashboard/webhooks'
                title='Webhooks'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWebhooksShowRoute}
                exact={false}
                path='/dashboard/webhooks/:webhookId'
                title='Webhooks'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWebhookEventsRoute}
                exact={true}
                path='/dashboard/webhook-events'
                title='Webhook events'
                v6
              />
              <AuthenticatedRoute
                component={DashboardWebhookEventsShowRoute}
                exact={false}
                path='/dashboard/webhook-events/:webhookEventId'
                title='Webhook events'
                v6
              />
              {/* Coverage Maps */}
              <AuthenticatedRoute
                component={DashboardResourcesCoverageMapRoute}
                exact={false}
                path='/dashboard/resources/coverage-map'
                redirectOnMissingCurrentEnvironment={false}
                title='Coverage map - Resources'
                v6
              />
              {/* Checks Table */}
              <AuthenticatedRoute
                component={DashboardResourcesChecksTableRoute}
                exact={false}
                path='/dashboard/resources/verification-checks'
                redirectOnMissingCurrentEnvironment={false}
                title='Verification checks - Resources'
                v6
              />
              {/* ADMIN - Team */}
              <AuthenticatedRoute
                component={DashboardUsersRoute}
                exact={true}
                path='/dashboard/users'
                redirectOnMissingCurrentEnvironment={false}
                title='Users'
                v6
              />
              <AuthenticatedRoute
                component={DashboardUserRolesRoute}
                exact={true}
                path='/dashboard/user-roles'
                redirectOnMissingCurrentEnvironment={false}
                title='Roles'
                v6
              />
              <AuthenticatedRoute
                component={DashboardUserRolesShowRoute}
                exact={false}
                path='/dashboard/user-roles/:roleId'
                redirectOnMissingCurrentEnvironment={false}
                title='Roles'
                v6
              />
              <AuthenticatedRoute
                component={DashboardUserSessionsRoute}
                exact={true}
                path='/dashboard/user-sessions'
                redirectOnMissingCurrentEnvironment={false}
                title='Sessions'
                v6
              />
              <AuthenticatedRoute
                component={DashboardUserAuditLogsRoute}
                exact={true}
                path='/dashboard/user-audit-logs'
                redirectOnMissingCurrentEnvironment={false}
                title='Audit logs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardUserAuditLogsShowRoute}
                exact={false}
                path='/dashboard/user-audit-logs/:userAuditLogId'
                redirectOnMissingCurrentEnvironment={false}
                title='Audit logs'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTeamShowRoute}
                exact={true}
                path='/dashboard/teams/:teamId'
                redirectOnMissingCurrentEnvironment={false}
                title='Teams'
                v6
              />
              <AuthenticatedRoute
                component={DashboardTeamsRoute}
                exact={true}
                path='/dashboard/teams'
                redirectOnMissingCurrentEnvironment={false}
                title='Teams'
                v6
              />
              {/* ADMIN - Organization */}
              <AuthenticatedRoute
                component={DashboardOrganizationInformationRoute}
                exact
                path='/dashboard/organization'
                redirectOnMissingCurrentEnvironment={false}
                title='Organization'
                v6
              />
              <AuthenticatedRoute
                component={DashboardOrganizationSecurityRoute}
                exact
                path='/dashboard/security'
                redirectOnMissingCurrentEnvironment={false}
                title='Security'
                v6
              />
              <AuthenticatedRoute
                component={DashboardOrganizationBillingRoute}
                path='/dashboard/billing'
                redirectOnMissingCurrentEnvironment={false}
                title='Billing'
                v6
              />
              <AuthenticatedRoute
                component={DashboardOrganizationEmailAddressesRoute}
                path='/dashboard/email-addresses'
                redirectOnMissingCurrentEnvironment={false}
                title='Email Addresses'
                v6
              />
              {/* FOOTER */}
              <AuthenticatedRoute
                component={() => <Navigate to='/dashboard/user/profile' replace />}
                exact
                path='/dashboard/user'
                title='Profile'
                v6
              />
              <AuthenticatedRoute
                component={DashboardUserProfileRoute}
                exact={false}
                path='/dashboard/user/profile'
                redirectOnMissingCurrentEnvironment={false}
                title='Profile'
                v6
              />
              {
                // ------------------------------
                // CATCH-ALL ROUTES
                // TODO: Update to react-router v6 when we switch from `<Switch>`
                // ------------------------------
              }
              <SentryRoute component={Http404Redirect} title='Not found' />
            </Switch>
          }
        />
      </SentryRoutes>
    </Suspense>
  );
};

export default Router;
